---
title: "A pricing experiment framework that won't *break* trust."
excerpt: "Grandfathering, cohort isolation, and honest comms let you test price without torching the customers who got you here."
category: "Pricing"
author: samuel
date: "2026-04-17"
readTime: "7 min read"
deck: "You can test pricing or you can keep your customers' trust. Done carefully, you *don't* have to choose."
---

Pricing is the highest-leverage lever a subscription business has, and the one teams are
most afraid to touch, because the downside is so visible. Test it carelessly and you
teach loyal customers that the price they agreed to was never real. The way out isn't to
avoid pricing experiments. It's to run them with enough structure that no existing
customer is ever made worse off by being a test subject.

## Grandfather first, always

The cardinal rule: a customer who is already paying should never be silently moved onto a
new price because you were experimenting. Grandfathering existing customers onto their
current terms, and applying changes only to new cohorts or at clearly communicated
renewal points, is what separates a pricing experiment from a breach of trust.

## Isolate the cohort under test

A clean experiment changes one thing for one well-defined group. Mixing new-customer
pricing tests with changes to existing accounts makes the results unreadable and the
fallout unpredictable.

- Define the cohort by signup date or segment, not by who happens to renew this week.
- Hold the rest of the book fixed so the control group is real.
- Decide the success metric, conversion, expansion, or retention, before you start.

> The fastest way to lose pricing power is to make your best customers feel like they
> overpaid.

## Communicate like you'd want to be told

When a change does reach existing customers, the comms matter as much as the price.
Advance notice, a clear reason, and a path that respects the relationship, an opt-in, a
transition period, or a held rate for loyal accounts, turn a price increase from a
betrayal into a routine business event. The framework is simple to state: protect the
people who got you here, test on the margin, and never surprise anyone with their own
invoice.
